import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Bell } from "@/shared/icons";
import { IconButton } from "@/shared/components/IconButton";
import { useAuth } from "@/modules/auth";
import { useChannel } from "@/modules/realtime";
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
  type NotificationRow,
} from "@/api/hooks/notifications";
import { formatDate } from "@/shared/utils/format";

function fmtRelative(iso: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return formatDate(iso);
}

function subjectHref(n: NotificationRow, role: string | undefined): string | null {
  if (!n.subjectType || !n.subjectId) return null;
  if (n.subjectType === "workunits.workunit") return `/work-units/${n.subjectId}`;
  if (n.subjectType === "leaves.leaverequest") return "/leaves";
  if (n.subjectType === "workunits.workunitrequest") {
    const projectId = (n.payload as Record<string, unknown> | null)?.projectId;
    return typeof projectId === "string" ? `/projects/${projectId}` : "/projects";
  }
  if (n.subjectType === "clientportal.clientdocument") {
    if (role === "client") return "/client-portal";
    const p = (n.payload as Record<string, unknown> | null) ?? {};
    const wuId = typeof p.workUnitId === "string" ? p.workUnitId : null;
    if (wuId) return `/work-units/${wuId}`;
    return "/client-docs";
  }
  return null;
}

export function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const popRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const unread = useUnreadCount();
  const list = useNotifications({});
  const markRead = useMarkRead();
  const markAll = useMarkAllRead();

  // Realtime: invalidate on push.
  useChannel(user?.id ? `user.${user.id}` : null, {
    "notification.created": () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    "notification.read": () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
    "notification.read_all": () => {
      qc.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const count = unread.data?.count ?? 0;
  const rows = list.data?.results ?? [];

  const onRowClick = async (n: NotificationRow) => {
    if (!n.readAt) {
      try {
        await markRead.mutateAsync(n.id);
      } catch {
        // ignore
      }
    }
    const href = subjectHref(n, user?.role);
    setOpen(false);
    if (href) navigate(href);
  };

  return (
    <div className="relative" ref={popRef}>
      <IconButton label="Notifications" onClick={() => setOpen((o) => !o)}>
        <Bell size={18} aria-hidden />
        {count > 0 && (
          <span
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: 999,
              background: "var(--danger-600)",
              color: "#fff",
              fontSize: 10,
              fontWeight: 700,
              lineHeight: "16px",
              textAlign: "center",
              boxShadow: "0 0 0 2px var(--surface)",
            }}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </IconButton>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            right: 0,
            top: 44,
            width: 380,
            maxHeight: 480,
            display: "flex",
            flexDirection: "column",
            background: "var(--surface)",
            border: "1px solid var(--line)",
            borderRadius: 8,
            boxShadow: "var(--shadow-md)",
            zIndex: 60,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 14px",
              borderBottom: "1px solid var(--line)",
              background: "var(--bg-page)",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)" }}>
              Notifications {count > 0 && <span style={{ color: "var(--ink-3)", fontWeight: 400 }}>· {count} unread</span>}
            </div>
            {count > 0 && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => markAll.mutate()}
                disabled={markAll.isPending}
                style={{ fontSize: 11, padding: "2px 8px" }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {list.isLoading ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--ink-3)", fontSize: 12 }}>Loading…</div>
            ) : rows.length === 0 ? (
              <div style={{ padding: 32, textAlign: "center", color: "var(--ink-3)", fontSize: 12 }}>
                No notifications.
              </div>
            ) : (
              rows.slice(0, 20).map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => void onRowClick(n)}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: 2,
                    width: "100%",
                    textAlign: "left",
                    padding: "10px 14px",
                    borderBottom: "1px solid var(--line)",
                    background: n.readAt ? "transparent" : "var(--accent-50)",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                    {!n.readAt && (
                      <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--accent-600)", flexShrink: 0 }} />
                    )}
                    <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {n.title}
                    </span>
                    <span style={{ fontSize: 10, color: "var(--ink-3)", flexShrink: 0 }}>{fmtRelative(n.createdAt)}</span>
                  </div>
                  {n.body && (
                    <div style={{ fontSize: 12, color: "var(--ink-2)", overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                      {n.body}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>

          <div
            style={{
              padding: "8px 14px",
              borderTop: "1px solid var(--line)",
              background: "var(--bg-page)",
              textAlign: "center",
            }}
          >
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              style={{ fontSize: 12, color: "var(--accent-700)", fontWeight: 500 }}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
